// TES-1317 (S4 of TES-1311) — step 6 (Ö-456; A-02's step 5, "Förhandsvisa
// och publicera till patienten"): exactly what the patient receives, and the warnings shown
// before the doctor publishes it. A-02 "FÖRTYDLIGANDE 2026-10-13":
//   "Steg 5 visar exakt det som publiceras till patienten:
//    - Sammanfattning efter vårdmötet.
//    - Behandlingsbeslut, när ett sådant har fattats.
//    - Läkarens kommentar till behandlingsbeslutet, när den finns.
//    - Vad händer nu?"
// No journal text: "Intern journaltext får inte blandas ihop med
// patientpublicerat innehåll."
//
// The rule for when the notice is published mirrors the server's
// (MeetingWorkflowService.documentMeeting, MeetingDecisionService), and the
// text comes from the same contract functions, so the preview is what she
// gets.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import {
  finalizeDoctorNotice,
  PATIENT_PACKAGE_CONTRADICTION_TEXT,
  patientPackageContradictions,
  whatHappensNext,
  withWhatHappensNext,
  type CompletionItem,
  type CompletionOutcome,
  type PatientPackageOutcome,
} from "@care-by-ella/contracts";

// CP-09: "Den aktiva adminhandlingen ska heta Godkänn och publicera."
export const APPROVE_AND_PUBLISH_CTA = "Godkänn och publicera";

export interface MeetingPatientPackageInput {
  voluntary: boolean;
  outcome: CompletionOutcome | null;
  /** The decision is published with the meeting (TES-1185). */
  decidesNow: boolean;
  /** The meeting was documented before: a re-documentation. */
  documentedAlready: boolean;
  /** The doctor's notice as it stands in the editor. */
  noticeBody: string;
  items: readonly CompletionItem[];
  labOrderConfirmed: boolean;
  subscriptionActive: boolean;
  /**
   * "Läkarens kommentar till behandlingsbeslutet" exactly as published, ""
   * when there is none (TES-1290).
   */
  commentText: string;
}

export interface MeetingPatientPackage {
  outcome: PatientPackageOutcome | null;
  /** The doctor's own text, as published (empty headings dropped). */
  summaryText: string;
  /** The generated lines under "Vad händer nu?"; empty when none is sent. */
  whatHappensNext: string[];
  /** The whole notice the patient receives; "" when none is published. */
  noticeText: string;
  commentText: string;
  /** What to read before publishing, in the doctor's words. */
  warnings: string[];
}

export function meetingPackageOutcome(
  input: Pick<
    MeetingPatientPackageInput,
    "voluntary" | "outcome" | "decidesNow"
  >,
): PatientPackageOutcome | null {
  if (input.voluntary || !input.outcome) return null;
  if (input.outcome === "proceed")
    return input.decidesNow ? "approved" : "proceed_undecided";
  return input.outcome;
}

export function meetingPatientPackage(
  input: MeetingPatientPackageInput,
): MeetingPatientPackage {
  const outcome = meetingPackageOutcome(input);
  const summaryText = input.voluntary
    ? ""
    : finalizeDoctorNotice(input.noticeBody);
  const lines = outcome
    ? whatHappensNext({
        outcome,
        subscriptionActive: input.subscriptionActive,
        items: outcome === "completion" ? input.items : [],
        labOrderConfirmed: input.labOrderConfirmed,
      })
    : [];
  // The decision always publishes its notice; the meeting's own publishes
  // one on its first documentation, and later only with a text of hers.
  const publishes =
    outcome === "approved" ||
    summaryText !== "" ||
    (lines.length > 0 && !input.documentedAlready);
  const noticeText = publishes ? withWhatHappensNext(summaryText, lines) : "";
  const warnings = patientPackageContradictions({
    outcome,
    texts: [summaryText, input.commentText],
  }).map((c) => PATIENT_PACKAGE_CONTRADICTION_TEXT[c]);
  return {
    outcome,
    summaryText,
    whatHappensNext: publishes ? lines : [],
    noticeText,
    commentText: input.commentText,
    warnings,
  };
}
